import React from 'react';
import { Card, Descriptions, Spin, Empty, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAmenityById } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';


interface AmenityTabProps {
  amenityId: string;
}

const AmenityTab: React.FC<AmenityTabProps> = ({ amenityId }) => {
  const { t } = useTranslation(['properties', 'common']);
  const { firestoreUser } = useAuth();

  const { data: amenity, isLoading, error } = useQuery({
    queryKey: ['amenity', amenityId],
    queryFn: () => getAmenityById(amenityId, firestoreUser!),
    enabled: !!amenityId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !amenity) {
    return (
      <Card>
        <Empty description={t('properties:amenity.notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('properties:amenity.fields.name')}>
          {amenity.name}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:amenity.fields.type')}>
          <Tag color="blue">{t(`properties:amenity.types.${amenity.type}`)}</Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:amenity.fields.status')}>
          <Tag color={amenity.status === 'active' ? 'green' : amenity.status === 'maintenance' ? 'orange' : 'red'}>
            {t(`properties:amenity.status.${amenity.status}`)}
          </Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:amenity.fields.description')}>
          {amenity.description}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:amenity.fields.capacity')}>
          {amenity.capacity} {t('properties:amenity.fields.people')}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:amenity.fields.block')}>
          {amenity.block?.name || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:amenity.fields.operatingHours')}>
          {amenity.operatingHours ? (
            <div>
              <div>{t('properties:amenity.fields.open')}: {amenity.operatingHours.open}</div>
              <div>{t('properties:amenity.fields.close')}: {amenity.operatingHours.close}</div>
              <div>{t('properties:amenity.fields.days')}: {amenity.operatingHours.days.join(', ')}</div>
            </div>
          ) : '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:amenity.fields.guideline')}>
          {amenity.guideline?.name || '-'}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('properties:amenity.fields.images')}>
          {amenity.images?.length ? `${amenity.images.length} image(s)` : '-'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default AmenityTab; 